import { useState, type FormEvent } from 'react'
import Modal from './Modal'
import { track } from '../utils/analytics'

interface FeedbackModalProps {
  onClose: () => void
  /** Where the modal was opened from, so the email says whether it followed a game. */
  context?: string
}

// FormSubmit needs no account: POST to /ajax/<your-email> once, click the activation
// link it mails you, and it hands back a random string to use instead — so the address
// never appears in the built HTML. VITE_FORMSUBMIT_ID takes either form.
// Unset (local dev) → the form explains itself instead of posting into the void.
const FORMSUBMIT_ID = import.meta.env.VITE_FORMSUBMIT_ID as string | undefined

const QUICK_ANSWERS = ['AI was too easy', 'AI was too slow', 'Something was confusing']

export default function FeedbackModal({ onClose, context = 'menu' }: FeedbackModalProps) {
  const [message, setMessage] = useState('')
  const [email, setEmail] = useState('')
  const [tags, setTags] = useState<string[]>([])
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle')

  const toggleTag = (tag: string) =>
    setTags((current) =>
      current.includes(tag) ? current.filter((t) => t !== tag) : [...current, tag],
    )

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!FORMSUBMIT_ID || state === 'sending') return
    setState('sending')

    try {
      const response = await fetch(`https://formsubmit.co/ajax/${FORMSUBMIT_ID}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          feedback: message,
          email: email || undefined,
          quick_answers: tags.join(', ') || 'none',
          context,
          site: 'chinesecheckers.xyz',
          _subject: `Chinese Checkers feedback (${context})`,
          // AJAX submissions don't work with FormSubmit's reCAPTCHA step.
          _captcha: 'false',
          _template: 'table',
        }),
      })
      // FormSubmit answers 200 with {"success":"false"} for a form that still needs
      // activation, so the status code alone isn't enough.
      const result = (await response.json()) as { success?: string; message?: string }
      if (!response.ok || String(result.success) !== 'true') {
        throw new Error(result.message ?? String(response.status))
      }
      setState('sent')
      track('feedback_submitted', { context, quick_answers: tags.join(',') || 'none' })
    } catch (err) {
      // Visitors get the friendly copy below; the real reason (needs activation,
      // rate limited, deactivated form…) goes to the console so it's diagnosable
      // without having to probe the endpoint by hand.
      console.warn('[feedback] submission failed:', err)
      setState('error')
    }
  }

  if (state === 'sent') {
    return (
      <Modal
        title="Thank you"
        onClose={onClose}
        actions={
          <button className="sidebar-primary" type="button" onClick={onClose}>
            Close
          </button>
        }
      >
        <p className="howto-lead">
          That went straight to my inbox. If you left an email I'll probably reply.
        </p>
      </Modal>
    )
  }

  return (
    <Modal title="Feedback" onClose={onClose}>
      <form className="feedback-form" onSubmit={handleSubmit}>
        <p className="feedback-lead">
          Was anything confusing, broken, too easy or too difficult? Tell me and I'll fix
          it.
        </p>

        <div className="feedback-chips" role="group" aria-label="Quick answers">
          {QUICK_ANSWERS.map((tag) => (
            <button
              key={tag}
              type="button"
              className="feedback-chip"
              aria-pressed={tags.includes(tag)}
              onClick={() => toggleTag(tag)}
            >
              {tag}
            </button>
          ))}
        </div>

        <label className="feedback-label">
          What did you think?
          <textarea
            className="feedback-input"
            name="feedback"
            required
            rows={4}
            maxLength={2000}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Was anything confusing, broken, too easy or too difficult?"
          />
        </label>

        <label className="feedback-label">
          Email, optional
          <input
            className="feedback-input"
            type="email"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Only if you'd like a reply"
          />
        </label>

        {!FORMSUBMIT_ID && (
          <p className="feedback-note">
            Feedback delivery isn't configured yet — set <code>VITE_FORMSUBMIT_ID</code> to
            enable sending.
          </p>
        )}
        {state === 'error' && (
          <p className="feedback-note feedback-note-error">
            That didn't send. Please try again in a moment.
          </p>
        )}

        <div className="modal-actions">
          <button
            className="sidebar-primary"
            type="submit"
            disabled={!FORMSUBMIT_ID || state === 'sending'}
          >
            {state === 'sending' ? 'Sending…' : 'Send feedback'}
          </button>
          <button className="sidebar-option" type="button" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </Modal>
  )
}
